/* eslint-disable react/jsx-curly-newline */
import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import PropTypes from "prop-types";
import { useQuery } from "react-query";
import { BiLoaderAlt } from "react-icons/bi";
import {
  selectConfigurationIdToShare,
  setConfiguration,
  setReadOnlyMode,
  showSnackbar,
  setReFetchConfigurations,
  setShowDeleteConfirmationPopup,
  selectShowDeleteConfirmationPopup,
  selectConfigurationTypeToShare
} from "../../../../Retrofit/store/slices/retrofit";
import Spinner from "../../Icons/Spinner/Spinner";
import SavedConfigCard from "./SavedConfigCard";
import { endpointPrefixes, getSavedConfigurationById, logEvent, updateConfigurationApi } from "../../../utils/api/api";
import EmailPopup from "../../EmailPopup/EmailPopup";
import CustomPopup from "../../CustomPopup/CustomPopup";
import t from "../../../utils/translation/translator";
import { selectEventsData, setConfigurationType, setFullScreen } from "../../../store/slices/common";
import { setCartesianConfiguration, setCartesianReadOnlyMode } from "../../../../Cartesian/store/slices/cartesian";
import { setMc12Configuration } from "../../../../Mc12/store/slices/mc12";
import { setIntegratedDrivesConfiguration } from "../../../../IntegratedDrives/store/slices/integrated_drives";

const SavedConfigurations = ({ isLoading, savedConfigurations, requestsStatus }) => {
  const dispatch = useDispatch();
  const [searchValue, setSearchValue] = useState("");

  const [appLoading, setAppLoading] = useState(false);

  const eventsData = useSelector(selectEventsData);
  const loadConfiguration = async (config, from, readOnly = false) => {
    console.log(config, from, readOnly);
    setAppLoading(true);
    logEvent(eventsData.locale, eventsData.types.reOpen, from, config?.configId);
    // load config from backend
    const loadedConfiguration = await getSavedConfigurationById({
      id: config.id
    });
    const { configurationType } = loadedConfiguration.configuration.configData;
    const { configuration } = loadedConfiguration;
    console.log(configurationType, configuration);
    setAppLoading(false);
    switch (configurationType) {
      case "RETROFIT":
        dispatch(setReadOnlyMode(readOnly || loadedConfiguration.configuration.configData.configStatus === "FINISHED"));
        dispatch(setConfiguration(configuration));
        dispatch(setConfigurationType(configurationType));
        break;
      case "CARTESIAN":
        dispatch(
          setCartesianReadOnlyMode(readOnly || loadedConfiguration.configuration.configData.configStatus === "FINISHED")
        );
        dispatch(setCartesianConfiguration(configuration));
        dispatch(setConfigurationType(configurationType));
        break;
      case "MC12":
        dispatch(setMc12Configuration(configuration));
        dispatch(setConfigurationType(configurationType));
        break;
      case "INTEGRATED_DRIVES":
        dispatch(setIntegratedDrivesConfiguration(configuration));
        dispatch(setConfigurationType(configurationType));
        break;
      default:
        console.log("No configuration type found");
        break;
    }
    dispatch(setFullScreen(true));
  };

  const configurationIdToShare = useSelector(selectConfigurationIdToShare);
  const configurationTypeToShare = useSelector(selectConfigurationTypeToShare);

  const {
    refetch: execFetchConfigurationById,
    isFetching: isFetchingConfigurationById,
    isLoading: isLoadingGetConfigurationById
  } = useQuery(
    "Get saved configuration by ID",
    async () => {
      try {
        // const fetchedConf = await getSavedConfigurationById(searchValue);
        // console.log("fetchedConf", fetchedConf.configuration);
        await loadConfiguration(
          {
            id: searchValue
          },
          eventsData.tags.fromId,
          true
        );
        return null;
      } catch (error) {
        dispatch(showSnackbar({ id: "NO_CONFIG" }));
      }
      return undefined;
    },
    {
      enabled: false
    }
  );

  const handleGetConfigurationById = () => {
    if (searchValue) {
      execFetchConfigurationById();
    } else {
      dispatch(showSnackbar({ id: "MANDATORY_FIELDS" }));
    }
  };

  // eslint-disable-next-line no-unused-vars
  const [showMenu, setShowMenu] = useState(false);

  const [confIdToDelete, setConfIdToDelete] = useState({
    id: null,
    configurationType: null
  });

  const showDeleteConfirmationPopup = useSelector(selectShowDeleteConfirmationPopup);

  const {
    refetch: execDeleteConfiguration,
    // eslint-disable-next-line no-unused-vars
    isFetching: isFetchingDeleteConfiguration,
    // eslint-disable-next-line no-unused-vars
    isLoading: isLoadingDeleteConfiguration
  } = useQuery(
    "DELETE : Get saved configuration by ID then change status then update it",
    async () => {
      try {
        logEvent(eventsData.locale, eventsData.types.deleteConf, eventsData.tags.none, confIdToDelete);
        const fetchedConf = await getSavedConfigurationById(confIdToDelete);
        // console.log("DELETE : fetchedConf", fetchedConf.configuration);
        fetchedConf.configuration.configData.configStatus = "OBSOLETE";
        const updatedConf = await updateConfigurationApi(confIdToDelete.id, fetchedConf.configuration.configData);
        // console.log("DELETE : updatedConf", updatedConf);
        dispatch(setReFetchConfigurations());
        setShowMenu(false);
        dispatch(setShowDeleteConfirmationPopup(false));
        return updatedConf;
      } catch (error) {
        console.error("ERR", error.response?.status);
        setShowMenu(false);
      }
      return undefined;
    },
    {
      enabled: false
    }
  );

  useEffect(() => {
    setAppLoading(
      isLoading ||
        isLoadingGetConfigurationById ||
        isFetchingConfigurationById ||
        isLoadingDeleteConfiguration ||
        isFetchingDeleteConfiguration
    );
  }, [
    isLoading,
    isLoadingGetConfigurationById,
    isFetchingConfigurationById,
    isLoadingDeleteConfiguration,
    isFetchingDeleteConfiguration
  ]);

  const [configurationsFilter] = useState(
    Object.keys(endpointPrefixes).reduce((acc, ep) => {
      acc[ep] = true;
      return acc;
    }, {})
  );

  // Function to update the confiuration filter for a specific prefix
  // const updateConfigurationFilter = (prefix, status) => {
  //   setConfigurationsFilter((prevStatus) => ({
  //     ...prevStatus,
  //     [prefix]: status
  //   }));
  // };

  let i = 0;

  return (
    <div className={`mx-2 m-auto ${appLoading ? "opacity-50 pointer-events-none" : ""}`}>
      <div className="flex flex-col md:flex-row items-center mb-5 w-full text-gray-700 text-sm">
        <div className="relative flex h-7 md:w-[24rem]">
          <input
            value={searchValue}
            onChange={(e) => setSearchValue(e.target.value)}
            className="w-full flex-1 pl-3 pr-12 placeholder-gray-400 border-[1px] border-gray-300 rounded placeholder-i text-sm"
            type="text"
            placeholder={t("msizer.ui.home.search_configuration.input_placeholder")}
            // on press enter => handleGetConfigurationById
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                handleGetConfigurationById();
              }
            }}
          />
          <button
            onClick={handleGetConfigurationById}
            type="button"
            className="absolute inset-y-0 text-center right-0 w-10 flex items-center text-xs border-[1px] border-gray-400 border-l-0 px-3 text-white bg-gray-400 hover:bg-gray-500 transition-all duration-100 rounded-r"
          >
            {isFetchingConfigurationById || isLoadingGetConfigurationById ? (
              <BiLoaderAlt className="animate-spin" />
            ) : (
              <span className="uppercase">{t("msizer.ui.home.search_configuration.button_text")}</span>
            )}
          </button>
        </div>
        {/* <div className="flex-1 flex justify-end items-center space-x-2">
          {Object.keys(endpointPrefixes).map((ep) => (
            <LabelSingleton
              key={ep}
              label={ep}
              number={
                requestsStatus[ep] === "SUCCESS" ? savedConfigurations.filter((c) => c.type === ep)?.length : null
              }
              selectedInit={requestsStatus[ep] === "SUCCESS"}
              // disabled={requestsStatus[ep] === "ERROR"}
              error={requestsStatus[ep] === "ERROR"}
              changeWithInit
              onSelect={() => {
                updateConfigurationFilter(ep, !configurationsFilter[ep]);
              }}
            />
          ))}
        </div> */}
        {/* <Button
          onClick={() => {
            dispatch(setReFetchConfigurations());
          }}
        >
          <MsxIcons name="refresh" className={`text-secondary ${isLoading && "animate-spin"}`} />
        </Button> */}
      </div>
      {isLoading && (
        <div className="text-center">
          <Spinner />
        </div>
      )}
      {!isLoading && (
        <>
          <div className="saved-configs-list grid grid-cols-1 md:grid-cols-3 md:gap-8 gap-4">
            {savedConfigurations.map((config, index) => {
              // change IN_PROGRESS to FINISHED
              i += 1;
              return (
                config.status === "FINISHED" &&
                configurationsFilter[config.type] === true && (
                  <SavedConfigCard
                    // eslint-disable-next-line react/no-array-index-key
                    key={index}
                    idLabel={i}
                    id={config.id}
                    title={`${config.name}`}
                    status={config.status}
                    date={config.createdDate}
                    country={config.country}
                    numberOfAxes={config.numberOfAxes}
                    configuration={config}
                    onClick={() => {
                      if (["CANCELLED", "OBSOLETE"].includes(config.status)) {
                        console.log("CANCELLED or OBSOLETE");
                      } else {
                        loadConfiguration(config, eventsData.tags.fromList);
                      }
                    }}
                    getConfIdToDelete={(id) =>
                      setConfIdToDelete({
                        id,
                        configurationType: config.configurationType
                      })
                    }
                    configurationType={config.type || "RETROFIT"}
                    kinematicsType={config.kinematicsType}
                  />
                )
              );
            })}
          </div>
          <CustomPopup
            opened={showDeleteConfirmationPopup}
            onClose={() => dispatch(setShowDeleteConfirmationPopup(false))}
            title={t("msizer.ui.popup.delete.config.confirmation.title")}
            text={t("msizer.ui.popup.delete.config.confirmation.text")}
            cancelLabel={t("msizer.ui.popup.button.cancel")}
            cancelAction={
              () => dispatch(setShowDeleteConfirmationPopup(false))
              // eslint-disable-next-line react/jsx-curly-newline
            }
            continueLabel={t("msizer.ui.popup.button.confirm")}
            continueAction={() => {
              execDeleteConfiguration();
            }}
          />
          <EmailPopup configId={configurationIdToShare || ""} configType={configurationTypeToShare || ""} />
        </>
      )}
    </div>
  );
};

SavedConfigurations.propTypes = {
  isLoading: PropTypes.bool,
  savedConfigurations: PropTypes.arrayOf(PropTypes.object),
  requestsStatus: PropTypes.object
};

SavedConfigurations.defaultProps = {
  isLoading: false,
  savedConfigurations: [],
  requestsStatus: {}
};

export default SavedConfigurations;
